import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  Plane, Briefcase, Mountain, Package, Clock, ArrowRight, Play,
  Shield, Star, UserRound, Sparkles, Lock, Globe2,
} from "lucide-react";
import { useLang } from "../i18n/LangProvider";
import { Reveal } from "../components/Reveal";
import { PriceCalculator } from "../components/PriceCalculator";
import { Vehicle3DViewer } from "../components/Vehicle3DViewer";

// Reference layout copy — matches the client-supplied hero design 1:1.
// Every image reference is used exactly ONCE across the page so nothing
// duplicates visually. The 5 canonical services below correspond to the
// five cards in the reference.
const COPY = {
  en: {
    kicker: "Business transfers & shuttles",
    headline1: "Premium transfers.", headline2a: "Across ", headline2b: "Switzerland",
    headline3a: "and ", headline3b: "beyond.",
    subtitle: "Discretion. Comfort. Reliability.  Your journey is our commitment.",
    badges: ["Safe & Secure", "On Time", "First Class"],
    servicesLabel: "Our Services",
    services: [
      { k: "airport",    icon: Plane,    img: "/images/byd-front-lucerne.jpg",
        title: "Airport transfers",         body: "Zurich, Geneva, Basel, Bern and more.",
        to: "/reservar?service=transfer" },
      { k: "business",   icon: Briefcase, img: "/images/byd-burgenstock.jpg",
        title: "Business travel",           body: "Executive transfers for professionals and companies.",
        to: "/contacto" },
      { k: "swiss",      icon: Mountain, img: "/images/byd-mountain-road.jpg",
        title: "Swiss destinations",        body: "Interlaken, Lucerne, St. Moritz, Gstaad and more.",
        to: "/reservar?service=multicity" },
      { k: "delivery",   icon: Package,  img: "/images/courier-banner.jpg",
        title: "Package & document delivery", body: "Fast, secure and discreet across Switzerland and Europe.",
        to: "/contacto" },
      { k: "hourly",     icon: Clock,    img: "/images/byd-rear-highway.jpg",
        title: "Hourly service",            body: "Available by the hour for flexible travel needs.",
        to: "/reservar?service=by_hour" },
    ],
    pillars: [
      { icon: UserRound, title: "Professional drivers", body: "Experienced, discreet and multilingual." },
      { icon: Sparkles,  title: "Premium comfort",       body: "BYD Seal U DM-i — spacious, quiet and modern." },
      { icon: Lock,      title: "Discretion guaranteed", body: "Your privacy is always our priority." },
      { icon: Globe2,    title: "Cross-border service",  body: "Switzerland, France, Italy and beyond." },
    ],
    exploreVehicle: "The vehicle",
    exploreVehicleBody: "Drag to rotate. Interior walkthrough below.",
    interiorLabel: "Interior · walkthrough",
    interiorTitle: "The cabin, up close.",
    interiorBody: "A short walkthrough of the cabin details — seats, controls, ambient lighting. Filmed inside the actual vehicle you'll travel in.",
    playLabel: "Watch",
    regionsLabel: "Business regions",
  },
  de: {
    kicker: "Business Transfers & Shuttles",
    headline1: "Premium-Transfers.", headline2a: "Durch die ", headline2b: "Schweiz",
    headline3a: "und ", headline3b: "darüber hinaus.",
    subtitle: "Diskretion. Komfort. Verlässlichkeit.  Ihre Fahrt ist unser Anspruch.",
    badges: ["Sicher", "Pünktlich", "First Class"],
    servicesLabel: "Unsere Services",
    services: [
      { k: "airport",    icon: Plane,    img: "/images/byd-front-lucerne.jpg",  title: "Flughafentransfers",       body: "Zürich, Genf, Basel, Bern und mehr.", to: "/reservar?service=transfer" },
      { k: "business",   icon: Briefcase, img: "/images/byd-burgenstock.jpg",   title: "Business Travel",           body: "Executive-Transfers für Profis und Unternehmen.", to: "/contacto" },
      { k: "swiss",      icon: Mountain, img: "/images/byd-mountain-road.jpg", title: "Schweizer Destinationen",   body: "Interlaken, Luzern, St. Moritz, Gstaad und mehr.", to: "/reservar?service=multicity" },
      { k: "delivery",   icon: Package,  img: "/images/courier-banner.jpg",     title: "Paket- & Dokumentenzustellung", body: "Schnell, sicher und diskret in der Schweiz und Europa.", to: "/contacto" },
      { k: "hourly",     icon: Clock,    img: "/images/byd-rear-highway.jpg",   title: "Stundenservice",            body: "Stundenweise verfügbar — flexibel nach Bedarf.", to: "/reservar?service=by_hour" },
    ],
    pillars: [
      { icon: UserRound, title: "Professionelle Chauffeurin", body: "Erfahren, diskret und mehrsprachig." },
      { icon: Sparkles,  title: "Premium-Komfort",             body: "BYD Seal U DM-i — geräumig, leise und modern." },
      { icon: Lock,      title: "Diskretion garantiert",       body: "Ihre Privatsphäre ist immer unsere Priorität." },
      { icon: Globe2,    title: "Grenzüberschreitend",         body: "Schweiz, Frankreich, Italien und darüber hinaus." },
    ],
    exploreVehicle: "Das Fahrzeug", exploreVehicleBody: "Ziehen zum Drehen. Innenraum-Rundgang unten.",
    interiorLabel: "Interior · Rundgang", interiorTitle: "Die Kabine, aus der Nähe.",
    interiorBody: "Ein kurzer Rundgang durch die Kabinendetails — Sitze, Bedienelemente, Ambientebeleuchtung.",
    playLabel: "Anschauen", regionsLabel: "Business-Regionen",
  },
  pt: {
    kicker: "Business transfers & shuttles",
    headline1: "Transfers premium.", headline2a: "Pela ", headline2b: "Suíça",
    headline3a: "e ", headline3b: "além.",
    subtitle: "Discrição. Conforto. Fiabilidade.  A sua viagem é o nosso compromisso.",
    badges: ["Seguro", "Pontual", "First Class"],
    servicesLabel: "Os nossos serviços",
    services: [
      { k: "airport",  icon: Plane,     img: "/images/byd-front-lucerne.jpg",  title: "Transfers aeroporto",          body: "Zurique, Genebra, Basileia, Berna e mais.", to: "/reservar?service=transfer" },
      { k: "business", icon: Briefcase, img: "/images/byd-burgenstock.jpg",   title: "Business travel",              body: "Transfers executivos para profissionais e empresas.", to: "/contacto" },
      { k: "swiss",    icon: Mountain,  img: "/images/byd-mountain-road.jpg", title: "Destinos suíços",              body: "Interlaken, Lucerna, St. Moritz, Gstaad e mais.", to: "/reservar?service=multicity" },
      { k: "delivery", icon: Package,   img: "/images/courier-banner.jpg",     title: "Entrega de encomendas & documentos", body: "Rápido, seguro e discreto na Suíça e na Europa.", to: "/contacto" },
      { k: "hourly",   icon: Clock,     img: "/images/byd-rear-highway.jpg",   title: "Serviço à hora",               body: "Disponível à hora, para deslocações flexíveis.", to: "/reservar?service=by_hour" },
    ],
    pillars: [
      { icon: UserRound, title: "Chauffeur profissional", body: "Experiente, discreta e multilingue." },
      { icon: Sparkles,  title: "Conforto premium",       body: "BYD Seal U DM-i — espaçoso, silencioso e moderno." },
      { icon: Lock,      title: "Discrição garantida",    body: "A sua privacidade é sempre a nossa prioridade." },
      { icon: Globe2,    title: "Serviço transfronteiriço", body: "Suíça, França, Itália e além." },
    ],
    exploreVehicle: "O veículo", exploreVehicleBody: "Arraste para rodar. Walkthrough do interior abaixo.",
    interiorLabel: "Interior · walkthrough", interiorTitle: "A cabine, de perto.",
    interiorBody: "Um walkthrough curto dos detalhes da cabine — bancos, comandos, iluminação ambiente.",
    playLabel: "Ver", regionsLabel: "Regiões business",
  },
  fr: {
    kicker: "Business transfers & shuttles",
    headline1: "Transferts premium.", headline2a: "À travers la ", headline2b: "Suisse",
    headline3a: "et ", headline3b: "au-delà.",
    subtitle: "Discrétion. Confort. Fiabilité.  Votre trajet est notre engagement.",
    badges: ["Sûr", "À l'heure", "First Class"],
    servicesLabel: "Nos services",
    services: [
      { k: "airport",  icon: Plane,     img: "/images/byd-front-lucerne.jpg",  title: "Transferts aéroport",         body: "Zurich, Genève, Bâle, Berne et plus.", to: "/reservar?service=transfer" },
      { k: "business", icon: Briefcase, img: "/images/byd-burgenstock.jpg",   title: "Voyage d'affaires",           body: "Transferts exécutifs pour professionnels et entreprises.", to: "/contacto" },
      { k: "swiss",    icon: Mountain,  img: "/images/byd-mountain-road.jpg", title: "Destinations suisses",        body: "Interlaken, Lucerne, St. Moritz, Gstaad et plus.", to: "/reservar?service=multicity" },
      { k: "delivery", icon: Package,   img: "/images/courier-banner.jpg",     title: "Livraison de colis & documents", body: "Rapide, sûr et discret en Suisse et en Europe.", to: "/contacto" },
      { k: "hourly",   icon: Clock,     img: "/images/byd-rear-highway.jpg",   title: "Service à l'heure",           body: "À l'heure, pour un besoin flexible.", to: "/reservar?service=by_hour" },
    ],
    pillars: [
      { icon: UserRound, title: "Chauffeuse professionnelle", body: "Expérimentée, discrète et multilingue." },
      { icon: Sparkles,  title: "Confort premium",             body: "BYD Seal U DM-i — spacieux, silencieux et moderne." },
      { icon: Lock,      title: "Discrétion garantie",         body: "Votre vie privée reste notre priorité." },
      { icon: Globe2,    title: "Service transfrontalier",     body: "Suisse, France, Italie et au-delà." },
    ],
    exploreVehicle: "Le véhicule", exploreVehicleBody: "Glissez pour tourner. Intérieur ci-dessous.",
    interiorLabel: "Intérieur · visite", interiorTitle: "L'habitacle, de près.",
    interiorBody: "Une courte visite des détails de l'habitacle — sièges, commandes, éclairage d'ambiance.",
    playLabel: "Regarder", regionsLabel: "Régions business",
  },
  it: {
    kicker: "Business transfers & shuttles",
    headline1: "Transfer premium.", headline2a: "In tutta la ", headline2b: "Svizzera",
    headline3a: "e ", headline3b: "oltre.",
    subtitle: "Discrezione. Comfort. Affidabilità.  Il vostro viaggio è il nostro impegno.",
    badges: ["Sicuro", "Puntuale", "First Class"],
    servicesLabel: "I nostri servizi",
    services: [
      { k: "airport",  icon: Plane,     img: "/images/byd-front-lucerne.jpg",  title: "Transfer aeroporto",         body: "Zurigo, Ginevra, Basilea, Berna e altre.", to: "/reservar?service=transfer" },
      { k: "business", icon: Briefcase, img: "/images/byd-burgenstock.jpg",   title: "Business travel",             body: "Transfer executive per professionisti e aziende.", to: "/contacto" },
      { k: "swiss",    icon: Mountain,  img: "/images/byd-mountain-road.jpg", title: "Destinazioni svizzere",       body: "Interlaken, Lucerna, St. Moritz, Gstaad e altro.", to: "/reservar?service=multicity" },
      { k: "delivery", icon: Package,   img: "/images/courier-banner.jpg",     title: "Consegna pacchi & documenti", body: "Rapido, sicuro e discreto in Svizzera ed Europa.", to: "/contacto" },
      { k: "hourly",   icon: Clock,     img: "/images/byd-rear-highway.jpg",   title: "Servizio ad ore",             body: "Ad ore, per esigenze flessibili.", to: "/reservar?service=by_hour" },
    ],
    pillars: [
      { icon: UserRound, title: "Chauffeur professionale", body: "Esperta, discreta e multilingue." },
      { icon: Sparkles,  title: "Comfort premium",         body: "BYD Seal U DM-i — spazioso, silenzioso e moderno." },
      { icon: Lock,      title: "Discrezione garantita",   body: "La vostra privacy è sempre la nostra priorità." },
      { icon: Globe2,    title: "Servizio transfrontaliero", body: "Svizzera, Francia, Italia e oltre." },
    ],
    exploreVehicle: "Il veicolo", exploreVehicleBody: "Trascina per ruotare. Interni sotto.",
    interiorLabel: "Interni · walkthrough", interiorTitle: "L'abitacolo, da vicino.",
    interiorBody: "Un breve walkthrough dei dettagli dell'abitacolo — sedili, comandi, luci ambient.",
    playLabel: "Guarda", regionsLabel: "Regioni business",
  },
};

const GOLD = "#B08D2F";

const Servicos = () => {
  const { lang } = useLang();
  const c = COPY[lang] || COPY.en;
  const [playing, setPlaying] = useState(false);

  return (
    <div className="pt-28 pb-24" data-testid="services-page">
      {/* HERO — matches client reference design */}
      <section className="relative border-b border-brand-border overflow-hidden" style={{ background: "#050505" }}>
        <div className="max-w-[1500px] mx-auto grid grid-cols-1 lg:grid-cols-[1fr_1.3fr] gap-0 items-stretch">
          {/* LEFT — text block */}
          <div className="px-8 lg:px-16 py-16 lg:py-24 flex flex-col justify-center relative z-10">
            <p className="text-[10px] tracking-[0.35em] uppercase mb-4 pl-1" style={{ color: GOLD }}>
              {c.kicker}
            </p>
            <div className="w-16 h-px mb-8" style={{ background: GOLD }} />
            <h1 className="display text-[2.5rem] md:text-[3.4rem] lg:text-[3.8rem] text-brand-platinum leading-[1.02] tracking-tight mb-8">
              {c.headline1}<br />
              {c.headline2a}<span style={{ color: GOLD }}>{c.headline2b}</span><br />
              {c.headline3a}<span style={{ color: GOLD }}>{c.headline3b}</span>
            </h1>
            <p className="text-brand-silver text-sm md:text-base leading-relaxed max-w-md mb-10 whitespace-pre-line">
              {c.subtitle}
            </p>
            <div className="flex flex-wrap gap-6 md:gap-10">
              {[Shield, Clock, Star].map((Ic, i) => (
                <div key={i} className="flex items-center gap-3">
                  <Ic size={16} style={{ color: GOLD }} strokeWidth={1.4} />
                  <span className="text-[10px] tracking-[0.25em] uppercase text-brand-platinum">{c.badges[i]}</span>
                </div>
              ))}
            </div>
          </div>
          {/* RIGHT — vehicle photo (single hero image — not reused elsewhere) */}
          <div className="relative min-h-[440px] lg:min-h-[560px] overflow-hidden">
            <img
              src="/images/byd-lucerne-hero.jpg"
              alt="BYD Seal U DM-i — SwissAlpenLine executive vehicle in Lucerne"
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0" style={{ background: "linear-gradient(90deg, rgba(5,5,5,0.6) 0%, transparent 30%, transparent 100%)" }} />
          </div>
        </div>
      </section>

      {/* OUR SERVICES — 5 unique cards, no duplicated imagery */}
      <section className="max-w-[1500px] mx-auto px-6 lg:px-10 mt-24">
        <Reveal>
          <div className="flex items-center justify-center mb-14">
            <span className="h-px w-16" style={{ background: GOLD, opacity: 0.6 }} />
            <p className="text-[11px] tracking-[0.35em] uppercase mx-6" style={{ color: GOLD }}>{c.servicesLabel}</p>
            <span className="h-px w-16" style={{ background: GOLD, opacity: 0.6 }} />
          </div>
        </Reveal>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4" data-testid="services-grid">
          {c.services.map((s, i) => {
            const Icon = s.icon;
            return (
              <Reveal key={s.k} delay={i * 80}>
                <Link
                  to={s.to}
                  data-testid={`service-card-${s.k}`}
                  className="group relative block h-full min-h-[420px] overflow-hidden border border-brand-border"
                >
                  <img
                    src={s.img}
                    alt={s.title}
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-[3500ms] group-hover:scale-105 kenburns"
                  />
                  <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(0,0,0,0.15) 0%, rgba(0,0,0,0.55) 55%, rgba(0,0,0,0.92) 100%)" }} />
                  <div className="relative z-10 h-full flex flex-col justify-between p-5">
                    <div
                      className="w-10 h-10 rounded-full flex items-center justify-center self-center border"
                      style={{ background: "#111", borderColor: GOLD, boxShadow: "0 0 0 3px rgba(176,141,47,0.10)" }}
                    >
                      <Icon size={16} style={{ color: GOLD }} strokeWidth={1.5} />
                    </div>
                    <div className="text-center">
                      <p className="text-[11px] tracking-[0.25em] uppercase text-brand-platinum mb-3">
                        {s.title}
                      </p>
                      <p className="text-[11px] md:text-xs text-brand-silver leading-relaxed">{s.body}</p>
                    </div>
                  </div>
                </Link>
              </Reveal>
            );
          })}
        </div>
      </section>

      {/* PILLARS — 4-column strip */}
      <section className="max-w-[1500px] mx-auto px-6 lg:px-10 mt-20 pb-4">
        <Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-x-10 gap-y-8 pt-10 border-t border-brand-border">
            {c.pillars.map((p) => {
              const Ic = p.icon;
              return (
                <div key={p.title} className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full flex items-center justify-center border shrink-0" style={{ borderColor: GOLD }}>
                    <Ic size={15} style={{ color: GOLD }} strokeWidth={1.4} />
                  </div>
                  <div>
                    <p className="text-[10px] tracking-[0.25em] uppercase text-brand-platinum mb-2">{p.title}</p>
                    <p className="text-xs text-brand-silver leading-relaxed">{p.body}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </Reveal>
      </section>

      {/* 3D VIEWER — interactive drag-to-rotate vehicle explorer */}
      <section className="max-w-[1500px] mx-auto px-6 lg:px-10 mt-32">
        <Reveal>
          <div className="mb-10">
            <p className="text-[10px] tracking-[0.35em] uppercase mb-3" style={{ color: GOLD }}>— {c.exploreVehicle}</p>
            <p className="text-brand-silver text-sm max-w-lg">{c.exploreVehicleBody}</p>
          </div>
          <Vehicle3DViewer lang={lang} />
        </Reveal>
      </section>

      {/* INTERIOR VIDEO */}
      <section className="max-w-[1500px] mx-auto px-6 lg:px-10 mt-24">
        <Reveal>
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.4fr] gap-10 items-center">
            <div>
              <p className="text-[10px] tracking-[0.35em] uppercase mb-3" style={{ color: GOLD }}>— {c.interiorLabel}</p>
              <h3 className="display text-3xl md:text-4xl text-brand-platinum leading-tight mb-5">{c.interiorTitle}</h3>
              <p className="text-brand-silver text-sm leading-relaxed mb-6">{c.interiorBody}</p>
              {!playing && (
                <button type="button" onClick={() => setPlaying(true)} data-testid="play-interior-video" className="btn-pill btn-pill--solid">
                  <Play size={14} /> {c.playLabel}
                </button>
              )}
            </div>
            <div className="border border-brand-border overflow-hidden aspect-video bg-black">
              <video
                key={playing ? "playing" : "poster"}
                data-testid="interior-video"
                src="/videos/byd-interior.mp4"
                controls={playing}
                autoPlay={playing}
                muted={playing}
                playsInline
                loop
                preload="metadata"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </Reveal>
      </section>

      {/* PRICE CALCULATOR */}
      <section className="max-w-[1500px] mx-auto px-6 lg:px-10 mt-24">
        <Reveal>
          <PriceCalculator />
        </Reveal>
      </section>
    </div>
  );
};

export default Servicos;
